import type { MergedGpEventResponse, GameClipIntervalRequest, EventListTeamInfo, GameEventCommentProps, GPGamePlayerResponse } from "../../../types";
import { VideoSidebar } from "../../../types";
import "./eventList.css";
import { CreateClipModal } from "./CreateClipModal";
import { ReportErrorModal } from "./ReportErrorModal/ReportErrorModal";
import type { ReportEventErrorHandler } from "../../../report-error";
import type { MediaPlayerInstance } from "@vidstack/react";
import clsx from "clsx";
import { _, _any } from "i18n";
import { useGameStore } from "../../../store";
import { useEditStore } from "../../../edit-store";
import { AttachModeSidebarView, useAttachStore } from "../../../attach-store";
import { isTeamOrPlayerFilterKey, ATTACH_CLIPS_MAX } from "../../../attach-mode";
import { EventListList } from "./EventListList";
import { EventCommentsPanel } from "../video-comments/EventCommentsPanel";
import { AttachModeLockBadge } from "../AttachModeLockBadge";
import { useCallback, type ChangeEvent, type MouseEvent, useEffect, useMemo, useRef } from "react";
import { Funnel } from "lucide-react";

interface EventListContainerProps {
	handleCreateClip?: (intervals: GameClipIntervalRequest[], name: string, description?: string) => void;
	onReportError?: ReportEventErrorHandler;
	playerRef: React.RefObject<MediaPlayerInstance> | null;
	isClip?: boolean;
	clipsPermission?: boolean;
	eventsPermission?: boolean;
	reportEventErrorPermission?: boolean;
	teamInfo: EventListTeamInfo;
	mergedEvents: MergedGpEventResponse[];
	eventCount: number;
	currentTime: number;
	eventCommentsPermission: boolean;
	gameEventComment?: GameEventCommentProps;
	players?: GPGamePlayerResponse[];
}

export function EventListContainer({
	clipsPermission,
	eventsPermission,
	reportEventErrorPermission,
	handleCreateClip,
	onReportError,
	playerRef,
	isClip,
	teamInfo,
	eventCount,
	mergedEvents,
	currentTime,
	eventCommentsPermission,
	gameEventComment,
	players,
}: EventListContainerProps) {
	const { selectedEvents, setSelectedEvents, showClipCreateModal, setShowClipCreateModal, showReportErrorModal, setShowReportErrorModal } = useEditStore();
	const { target: attachTarget, setSidebarView: setAttachSidebarView } = useAttachStore();
	const isAttachMode = attachTarget !== null;

	const { filteredFilterValues, filterValues, eventFilter, followVideo, setFollowVideo, sidebar, setSidebar, sidebarCommentEventId, setSidebarCommentEventId, gameDataLazy } = useGameStore();
	const canCreateClip = Boolean(clipsPermission && handleCreateClip) && isAttachMode === false;
	const canReportError = Boolean(reportEventErrorPermission && onReportError) && isAttachMode === false;
	const canBulkSelect = Boolean(clipsPermission) && isAttachMode === false;
	const selectionPermission = Boolean(clipsPermission || canReportError) || isAttachMode;
	const selectedFiltersCount = useMemo(() => {
		return filterValues.reduce((count, filterValue) => {
			if (isAttachMode && isTeamOrPlayerFilterKey(eventFilter, filterValue.key)) {
				return count;
			}

			return count + filterValue.value.length;
		}, 0);
	}, [eventFilter, filterValues, isAttachMode]);
	const eventCommentsAllowed = eventCommentsPermission && isAttachMode === false;
	const shouldRenderCreateClipModal = Boolean(showClipCreateModal && canCreateClip);

	const selectedReportEvent = useMemo(() => selectedEvents[selectedEvents.length - 1] ?? null, [selectedEvents]);

	const selectedReportPlayer = useMemo(() => {
		if (!selectedReportEvent || players === undefined || players.length === 0) {
			return null;
		}

		return (
			players.find((player) => player.id === selectedReportEvent.gamePlayerId) ??
			players.find(
				(player) =>
					player.teamId === selectedReportEvent.gameTeamId &&
					player.numberSign === selectedReportEvent.backNumber,
			) ??
			null
		);
	}, [players, selectedReportEvent]);
	const canShowReportErrorModal = Boolean(
		canReportError &&
		selectedReportEvent &&
		selectedEvents.length > 0,
	);
	const shouldRenderReportErrorModal = Boolean(
		showReportErrorModal &&
		canShowReportErrorModal &&
		!shouldRenderCreateClipModal,
	);
	const selectionStatusLabel =
		selectedEvents.length === 0
			? ""
			: selectedEvents.length === 1
				? _any("reportError.selectionStatusSingle")
				: _any("reportError.selectionStatusMultiple", { count: String(selectedEvents.length) });
	const isEventListLimited = isAttachMode || selectedFiltersCount > 0;
	const eventCountLabel =
		isEventListLimited === false
			? _("label.gamePage.allEventsPlaying")
			: `${eventCount} ${_("label.gamePage.eventsPlaying")}`;
	const allEventsSelected = mergedEvents.length > 0 && selectedEvents.length === mergedEvents.length;
	const isPartialEventSelection =
		selectedEvents.length > 0 && selectedEvents.length < mergedEvents.length;
	const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

	const mergedEventForComments = useMemo(
		() =>
			sidebarCommentEventId != null ? mergedEvents.find((e) => e.id === sidebarCommentEventId) ?? null : null,
		[mergedEvents, sidebarCommentEventId]
	);

	useEffect(() => {
		if (sidebarCommentEventId != null && !mergedEvents.some((e) => e.id === sidebarCommentEventId)) {
			setSidebarCommentEventId(null);
		}
	}, [mergedEvents, sidebarCommentEventId, setSidebarCommentEventId]);

	const handleOpenComments = useCallback(
		(event: MergedGpEventResponse) => {
			if (sidebar !== VideoSidebar.EventList && sidebar !== VideoSidebar.EventsWithComments) {
				setSidebar(VideoSidebar.EventList);
			}
			setSidebarCommentEventId(event.id);
		},
		[sidebar, setSidebar, setSidebarCommentEventId]
	);

	const handleOnSelectEvent = (item: MergedGpEventResponse) => {
		setSelectedEvents((prevSelected) => {
			const isSelected = prevSelected.some((i) => i.id === item.id);

			if (isSelected) {
				return prevSelected.filter((i) => i.id !== item.id);
			}

			if (isAttachMode && prevSelected.length >= ATTACH_CLIPS_MAX) {
				return prevSelected;
			}

			return [...prevSelected, item];
		});
	};

	const handleOnReportError = (event: MouseEvent<HTMLButtonElement>) => {
		event.currentTarget.blur();
		setShowReportErrorModal(true);
		playerRef?.current?.pause();
	};

	const handleOpenFilters = () => {
		if (isAttachMode) {
			setAttachSidebarView(AttachModeSidebarView.Filters);
			setSidebar(VideoSidebar.AttachMode);
			return;
		}

		setSidebar(VideoSidebar.Filters);
	};

	const handleOnSelectAllChange = (event: ChangeEvent<HTMLInputElement>): void => {
		if (event.target.checked) {
			setSelectedEvents(mergedEvents);
			return;
		}

		setSelectedEvents([]);
	};

	useEffect(() => {
		if (selectAllCheckboxRef.current) {
			selectAllCheckboxRef.current.indeterminate = isPartialEventSelection;
		}
	}, [isPartialEventSelection]);

	useEffect(() => {
		if (showClipCreateModal && !canCreateClip) {
			setShowClipCreateModal(false);
		}
	}, [canCreateClip, setShowClipCreateModal, showClipCreateModal]);

	useEffect(() => {
		if (showReportErrorModal && !canShowReportErrorModal) {
			setShowReportErrorModal(false);
		}
	}, [canShowReportErrorModal, setShowReportErrorModal, showReportErrorModal]);

	return (
		<div className={clsx("eventList-container", mergedEventForComments && "eventList-container--comments")}>
			{mergedEvents.length > 10 && !mergedEventForComments && (
				<span
					className={clsx("eventList__follow", { "eventList__follow--active": followVideo })}
					onClick={() => setFollowVideo(!followVideo)}
				>
					<span className="icon icon-10 icon-arrow-up" />
					<span className="icon icon-10 icon-arrow-down" />
				</span>
			)}
			{!isClip && !mergedEventForComments && (
				<div className={clsx("eventList__header", { "eventList__header--attach": isAttachMode })}>
					{isAttachMode && attachTarget !== null ? (
						<>
							<AttachModeLockBadge target={attachTarget} />
							{eventsPermission && (
								<button
									type="button"
									className="eventList__filtersButton"
									onClick={handleOpenFilters}
								>
									<span>{_("gamePage.label.filters")}</span>
									<span className="eventList__filtersButtonIconWrap">
										<Funnel className="eventList__filtersButtonIcon" aria-hidden />
										{selectedFiltersCount > 0 && (
											<span className="eventList__filtersCount">{selectedFiltersCount}</span>
										)}
									</span>
								</button>
							)}
						</>
					) : (
						<>
							{eventsPermission && (
								<button type="button" className="eventList__filtersButton" onClick={handleOpenFilters}>
									<span>{_("gamePage.label.filters")}</span>
									<span className="eventList__filtersButtonIconWrap">
										<Funnel className="eventList__filtersButtonIcon" aria-hidden />
										{selectedFiltersCount > 0 && (
											<span className="eventList__filtersCount">{selectedFiltersCount}</span>
										)}
									</span>
								</button>
							)}
						</>
					)}
				</div>
			)}
			{!mergedEventForComments && (
				<div className="eventList__eventCountRow">
					<span className="eventList__eventCount">{eventCountLabel}</span>
					{canBulkSelect && mergedEvents.length > 0 && (
						<label className="eventList__selectAll">
							<span>{_("eventList.action.selectAll")}</span>
							<input
								ref={selectAllCheckboxRef}
								type="checkbox"
								checked={allEventsSelected}
								onChange={handleOnSelectAllChange}
							/>
						</label>
					)}
				</div>
			)}
			{shouldRenderCreateClipModal && showClipCreateModal ? (
				handleCreateClip && <CreateClipModal close={() => setShowClipCreateModal(false)} onSubmit={handleCreateClip} />
			) : mergedEventForComments ? (
			<EventCommentsPanel
				key={mergedEventForComments.id}
				mergedEvent={mergedEventForComments}
				comments={mergedEventForComments.comments}
				onUpdate={(data) => {
					gameEventComment?.create(mergedEventForComments.id, data);
				}}
				onDeleteComment={(id: number, eventId: number) => {
					gameEventComment?.delete(id, eventId);
				}}
				onEditComment={(id, data) => {
					gameEventComment?.edit(id, data);
				}}
				players={players ?? []}
				isLoading={gameEventComment?.isLoading}
				commentFormDefaults={gameDataLazy?.commentFormDefaults}
			/>
			) : (
				<EventListList
					selectionAllowed={selectionPermission}
					selectionAtMax={isAttachMode && selectedEvents.length >= ATTACH_CLIPS_MAX}
					filteredFilterValues={filteredFilterValues}
					isClip={isClip || false}
					mergedEvents={mergedEvents}
					onWheel={setFollowVideo}
					playerRef={playerRef}
					teamInfo={teamInfo}
					selectedEvents={selectedEvents}
					onSelectEvent={handleOnSelectEvent}
					currentTime={currentTime}
					eventCommentsPermission={eventCommentsAllowed}
					onOpenComments={handleOpenComments}
				/>
			)}
			{shouldRenderReportErrorModal && (
				<ReportErrorModal
					show={showReportErrorModal}
					close={() => setShowReportErrorModal(false)}
					selectedEvent={selectedReportEvent}
					selectedEvents={selectedEvents}
					selectedPlayer={selectedReportPlayer}
					players={players}
					teamInfo={teamInfo}
					onSubmit={onReportError}
				/>
			)}

			{selectionPermission && selectedEvents.length !== 0 && !shouldRenderCreateClipModal && isAttachMode === false && (
				<div className="clipCreateContorls clipCreateContorls--selectionBar">
					<div className="clipCreateContorls__top">
						<span className="clipCreateContorls__status">
							{selectionStatusLabel}
						</span>
						{canReportError && (
							<button
								type="button"
								className="clipCreateContorls__report"
								onClick={handleOnReportError}
							>
								<span className="clipCreateContorls__reportIcon" aria-hidden />
								{_any("reportError.selectionReport")}
							</button>
						)}
					</div>
					<div className="clipCreateContorls__actions">
						<button className="clipButton cancel" type="button" onClick={() => setSelectedEvents([])}>
							{_("btn.cancel")}
						</button>
						{clipsPermission && (
							<button
								className="clipButton create"
								type="button"
								onClick={() => {
									setShowClipCreateModal(true);
									playerRef?.current?.pause();
								}}
							>
								{_("eventList.action.createClip")}
							</button>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
